/**
 * Transaction guard for safe refreshes.
 *
 * This does NOT introduce a second transaction system: it reads the ones the
 * app already has — React Query's mutation cache (POS sales, orders, credit
 * and timber writes) plus a single `data-transaction-active` marker that the
 * few screens holding in-flight work in local state (M-Pesa payment, website
 * checkout, the store editor's unsaved changes) already expose.
 */
import { useEffect } from 'react'
import type { QueryClient } from '@tanstack/react-query'

export const TRANSACTION_ATTR = 'data-transaction-active'

/** Mark the document as busy while `active` — used by the screens below. */
export function useTransactionGuard(active: boolean): void {
  useEffect(() => {
    if (!active) return
    const root = document.documentElement
    const previous = root.getAttribute(TRANSACTION_ATTR)
    root.setAttribute(TRANSACTION_ATTR, 'true')
    return () => {
      // Only clear what we set: overlapping guards must not unlock a refresh
      // while another one is still running.
      if (previous === null) root.removeAttribute(TRANSACTION_ATTR)
      else root.setAttribute(TRANSACTION_ATTR, previous)
    }
  }, [active])
}

/** True while React Query has a write in flight (sale being recorded, …). */
export function hasPendingMutations(queryClient?: QueryClient): boolean {
  if (!queryClient) return false
  return queryClient.getMutationCache().getAll().some((mutation) => mutation.state.status === 'pending')
}

/**
 * The single question the update manager asks: may we reload this tab right
 * now without risking a sale, a payment or unsaved input?
 */
export function isSafeToRefresh(queryClient?: QueryClient): boolean {
  if (typeof document === 'undefined') return true
  if (document.documentElement.getAttribute(TRANSACTION_ATTR) === 'true') return false
  if (document.querySelector(`[${TRANSACTION_ATTR}="true"]`)) return false
  if (hasPendingMutations(queryClient)) return false
  return true
}
